/**
 * Signature requests — V98-1-6 / ADR-0046 §5 (routing), §7 (append-only
 * records; a signature binds item version + facts version; a delegated
 * signature records "for X under delegation <id>"); D-98-10 (HMAC at freeze
 * and again at signing); research §5.1 principle 7 (signable artifacts are
 * versioned and frozen; clearing a prerequisite makes the frozen version
 * obsolete; signatures, send-backs and undos propagate to every view).
 *
 * Lifecycle of one frozen version (migration 232):
 *
 *   freeze ─► requested ──sign──► signed ──downstream send──► (final)
 *                 │  ▲               │
 *                 │  └────undo───────┘  (only before the send marker and undo_until)
 *                 ├──send back (reason)──► returned ──resubmit──► (version v+1, requested)
 *                 ├──withdraw (reason)───► withdrawn
 *                 └──prerequisite/facts change, or a newer freeze──► obsolete
 *
 * Routing (T1, V98-1-9): the SoD engine (`sod-service` `route`) walks
 * `approver` holders → their delegate when absent → `backup_approver`
 * holders → the approver's escalation chain, skipping absent people, the
 * item's parties for its pair (preparer; policy author; risk owner; SCN
 * classifier), the requester, non-members and audit-firm identities (T17).
 * No candidate → `none_eligible` (the request is kept, unsigned, with its
 * routing trail and reasons; the preparer may then pursue the alternatives →
 * compensating review in coverage-service). The DB CHECKs repeat the "never
 * the preparer" rule. A request routed via a delegate keeps the approver of
 * record in `approver_user_id`; the delegate signs "for X under delegation
 * <id>". Each routing writes a T1 `sod_events` row.
 *
 * Delegated signing (T4, V98-1-10): on_behalf_of + delegation_id, checked by
 * `verifyDelegationForRequest` (live, set by the delegator, covers the
 * artifact kind — never a non-delegable one — and the delegate is not a party
 * of the item). While a live delegation covers an item, the approver of record
 * is locked out of it until they take it back (end the delegation).
 *
 * A send-back RETURNS the request to its author (state `returned`, migration
 * 245; the event is still named `sent_back`): it is in nobody's approver
 * queue, absence preview, at-risk list or next-hard-date projection. The
 * author owes a "revise" work item; resubmitting freezes version v+1 of the
 * same artifact (re-routed) and closes that work item (V98-3 review S3).
 *
 * Not here yet:
 *   - Passkeys / codes are NOT verified server-side (TODO WebAuthn). V98-7-3
 *     M9: a signature is therefore recorded and HMAC-sealed as `method =
 *     'session'` (the CSRF-protected session user signed it) and the person's
 *     choice is kept as `method_claimed`; `presentedSignatureMethod` makes
 *     every surface report `session`, including rows sealed before 248.
 */
import { generateRandomId } from '../utils/audit-crypto.js';
import { isAuditFirmIdentity, subjectExists } from './assignment-service.js';
import { canonicalJson, sha256Hex } from './certification-package-service.js';
import { conflictsFor, effectiveDelegationFor, isAuthorizedApprover, route, signatureStep, sodEventStatement, verifyDelegationForRequest, } from './sod-service.js';
import { completeWorkItem, createWorkItem } from './work-item-service.js';
// Runtime-only use (function body) — package-lifecycle-service imports this module too.
import { prerequisitesForRequest } from './package-lifecycle-service.js';
/**
 * V98-7-3 M8: a certification package is signed / released only while no
 * blocking prerequisite is open. Re-derived now (`prerequisitesForRequest`
 * runs syncCauses; a new blocker also makes an open freeze obsolete), so a
 * change notice or expiring acceptance that appeared after the freeze stops
 * the stale version. 409 `prerequisites_open`.
 */
export async function assertPackagePrerequisitesClear(db, workspaceId, r, now = new Date()) {
    if (r.artifact_kind !== 'certification_package')
        return;
    const pre = await prerequisitesForRequest(db, workspaceId, r, now);
    if (pre && pre.blocking > 0) {
        throw new SignatureStateError('prerequisites_open', `${pre.blocking} prerequisite${pre.blocking === 1 ? '' : 's'} opened since this version was frozen — clear ${pre.blocking === 1 ? 'it' : 'them'} and freeze again.`);
    }
}
export const SIGNABLE_ARTIFACT_KINDS = [
    'certification_package', 'system_description', 'policy', 'scn', 'access_review',
    'risk_acceptance', 'management_response',
    // v10.2 / ADR-0058 §5: a CMMC affirmation, signed only by the named affirming official.
    'cmmc_affirmation',
];
/**
 * ADR-0058 §5: kinds signed by ONE person the frozen content names
 * (`official_user_id`), not by whoever holds the approver hat. Routing goes
 * to that person only (never a delegate, backup or fallback); signing checks
 * the signer is that person; the preparer and requester can never be them.
 */
export const NAMED_SIGNER_KINDS = new Set(['cmmc_affirmation']);
/** The named signer recorded in a frozen named-signer request (null for other kinds). */
export function namedSignerOf(r) {
    if (!NAMED_SIGNER_KINDS.has(r.artifact_kind) || !r.frozen_content_json)
        return null;
    try {
        const v = JSON.parse(r.frozen_content_json).official_user_id;
        return typeof v === 'string' && v ? v : null;
    }
    catch {
        return null;
    }
}
export const SIGNATURE_STATES = ['requested', 'signed', 'returned', 'withdrawn', 'obsolete'];
export const SIGNATURE_METHODS = ['passkey', 'code'];
/**
 * V98-7-3 M9: the method any surface may show. Nothing is verified beyond the
 * session yet, so every signed row reads `session` (a row sealed as 'passkey'
 * before migration 248 included) — never "signed with passkey".
 */
export function presentedSignatureMethod(method) {
    return method ? 'session' : null;
}
// ---- errors --------------------------------------------------------------------
export class SignatureValidationError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'SignatureValidationError';
    }
}
export class SignatureNotFoundError extends Error {
    constructor(id) {
        super(`Signature request not found: ${id}`);
        this.name = 'SignatureNotFoundError';
    }
}
/** The request isn't in a state that allows this transition. */
export class SignatureStateError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'SignatureStateError';
    }
}
/** The caller is a member but not the actor this step requires. */
export class SignatureForbiddenError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'SignatureForbiddenError';
    }
}
// ---- helpers -------------------------------------------------------------------
const KIND_SET = new Set(SIGNABLE_ARTIFACT_KINDS);
const REASON_MAX = 2000;
const FREEZE_SCHEMA = 'gacpro/signature-freeze@1';
const SIGN_SCHEMA = 'gacpro/signature@1';
export function isSignableArtifactKind(v) {
    return typeof v === 'string' && KIND_SET.has(v);
}
function cleanReason(v, required) {
    const r = (v ?? '').trim();
    if (!r) {
        if (required)
            throw new SignatureValidationError('reason_required', 'A reason is required');
        return null;
    }
    if (r.length > REASON_MAX)
        throw new SignatureValidationError('reason_too_long', `reason must be at most ${REASON_MAX} characters`);
    return r;
}
function checkId(v, field) {
    if (typeof v !== 'string' || v.length < 1 || v.length > 200) {
        throw new SignatureValidationError(`invalid_${field}`, `${field} must be 1–200 characters`);
    }
    return v;
}
function timingSafeEqualHex(a, b) {
    if (a.length !== b.length)
        return false;
    let diff = 0;
    for (let i = 0; i < a.length; i++)
        diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
    return diff === 0;
}
/** What the freeze HMAC covers: this version of this artifact, in this tenant. */
export function freezePayload(r) {
    return canonicalJson({
        artifact_id: r.artifact_id, artifact_kind: r.artifact_kind, content_sha256: r.content_sha256,
        facts_version: r.facts_version, request_id: r.request_id, schema: FREEZE_SCHEMA, version: r.version,
        workspace_id: r.workspace_id,
    });
}
/** What the signing HMAC covers: item version + facts version + the frozen hash + who signed, how, for whom. */
export function signaturePayload(r) {
    return canonicalJson({
        artifact_id: r.artifact_id, artifact_kind: r.artifact_kind, content_sha256: r.content_sha256,
        delegation_id: r.delegation_id, facts_version: r.facts_version, frozen_hash: r.frozen_hash, method: r.method,
        on_behalf_of_user_id: r.on_behalf_of_user_id, request_id: r.request_id, schema: SIGN_SCHEMA,
        signed_at: r.signed_at, signed_by_user_id: r.signed_by_user_id, version: r.version, workspace_id: r.workspace_id,
    });
}
async function signWithWorkspaceKey(db, sign, workspaceId, data) {
    const { key_id, signature } = await sign(workspaceId, data);
    // The signer must use this workspace's key — never another tenant's.
    const key = (await db.query(`SELECT workspace_id FROM hmac_keys WHERE key_id = ? AND workspace_id = ? LIMIT 1`, [key_id, workspaceId])).results[0];
    if (!key || key.workspace_id !== workspaceId) {
        throw new SignatureValidationError('foreign_key', 'Signing key does not belong to this workspace.');
    }
    if (!/^[a-f0-9]{64}$/.test(signature))
        throw new SignatureValidationError('bad_signature', 'Invalid signature from the signer.');
    return { key_id, signature };
}
async function isActiveMember(db, workspaceId, userId) {
    const r = await db.query(`SELECT user_id FROM memberships WHERE workspace_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [workspaceId, userId]);
    return r.results.length > 0;
}
function eventInsert(e, guard) {
    // With a guard the event row is written only if the transition's UPDATE in
    // the same batch actually landed (row now in `state`, stamped `updated_at`).
    const cols = `(event_id, workspace_id, request_id, version, event, actor_user_id, on_behalf_of_user_id, delegation_id, reason, before_json, after_json, at)`;
    const vals = [generateRandomId('sre'), e.workspace_id, e.request_id, e.version, e.event, e.actor_user_id,
        e.on_behalf_of_user_id, e.delegation_id, e.reason, e.before_json, e.after_json, e.at];
    if (!guard) {
        return {
            sql: `INSERT INTO signature_request_events (event_id, workspace_id, request_id, version, event, actor_user_id, on_behalf_of_user_id, delegation_id, reason, before_json, after_json, at)
            VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            params: vals,
        };
    }
    return {
        sql: `INSERT INTO signature_request_events ${cols}
          SELECT ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?
           WHERE EXISTS (SELECT 1 FROM signature_requests WHERE request_id = ? AND workspace_id = ? AND state = ? AND updated_at = ?)`,
        params: [...vals, e.request_id, e.workspace_id, guard.state, guard.updated_at],
    };
}
const snap = (r) => JSON.stringify({
    state: r.state, approver_user_id: r.approver_user_id, signed_by_user_id: r.signed_by_user_id, signed_at: r.signed_at,
    signature_key_id: r.signature_key_id, signature_hmac: r.signature_hmac, method: r.method,
    on_behalf_of_user_id: r.on_behalf_of_user_id, delegation_id: r.delegation_id, undo_until: r.undo_until,
    downstream_sent_at: r.downstream_sent_at, downstream_ref: r.downstream_ref, signed_record_ref: r.signed_record_ref, reason: r.reason,
});
async function load(db, workspaceId, requestId) {
    const r = await db.query(`SELECT * FROM signature_requests WHERE request_id = ? AND workspace_id = ? LIMIT 1`, [requestId, workspaceId]);
    if (!r.results[0])
        throw new SignatureNotFoundError(requestId);
    return r.results[0];
}
/** Run a guarded transition UPDATE + its event in one batch; throws if the row moved underneath. */
async function transition(db, update, event, guard) {
    const res = await db.batch([update, eventInsert(event, guard)]);
    if ((res?.[0]?.meta?.changes ?? 1) === 0) {
        throw new SignatureStateError('concurrent_change', 'The signature request changed while this action was in progress.');
    }
}
/**
 * T1 for a signature request: the SoD engine with the step and parties for
 * this artifact kind (preparer ↔ approver; policy author ↔ approver; risk
 * owner ↔ acceptance approver; SCN classifier ↔ co-approver). The requester
 * is never eligible either.
 */
export async function routeApprover(db, workspaceId, subject, exclude, now = new Date(), artifactKind = 'certification_package', namedSignerUserId = null) {
    if (NAMED_SIGNER_KINDS.has(artifactKind))
        return routeNamedSigner(db, workspaceId, namedSignerUserId, exclude, now);
    const { step, parties } = signatureStep(artifactKind, exclude.preparer_user_id);
    const r = await route(db, {
        workspace_id: workspaceId, subject, step, parties, category: artifactKind,
        exclude: [exclude.requested_by_user_id], now,
    });
    if (r.status !== 'routed') {
        return { status: 'none_eligible', approver_user_id: null, via: null, delegate_user_id: null, delegation_id: null, reasons: r.reasons, trail: r.trail };
    }
    const delegated = r.via === 'delegate';
    return {
        status: 'routed', approver_user_id: delegated ? r.on_behalf_of_user_id : r.actor_user_id, via: r.via,
        delegate_user_id: delegated ? r.actor_user_id : null, delegation_id: r.delegation_id, reasons: r.reasons, trail: r.trail,
    };
}
/**
 * ADR-0058 §5: a named-signer request routes to the named person only. The
 * preparer / requester pair and the eligibility rules of T1 still apply; there
 * is no delegate, backup, escalation or owner fallback, and absence does not
 * re-route it (the named official signs, or nobody does).
 */
async function routeNamedSigner(db, workspaceId, user, exclude, now) {
    const none = (outcome, reason) => ({
        status: 'none_eligible', approver_user_id: null, via: null, delegate_user_id: null, delegation_id: null, reasons: [reason],
        trail: user ? [{ user_id: user, responsibility: 'named_signer', via: 'holder', outcome, ...(outcome === 'skipped_preparer' ? { pair_ids: ['preparer_approver'] } : {}) }] : [],
    });
    if (!user)
        return none('skipped_no_account', 'no_holder: the request names no signer');
    if (user === exclude.preparer_user_id)
        return none('skipped_preparer', `conflict: ${user} (preparer_approver)`);
    if (user === exclude.requested_by_user_id)
        return none('skipped_excluded', `excluded: ${user}`);
    if (!(await isActiveMember(db, workspaceId, user)))
        return none('skipped_not_member', `not_member: ${user}`);
    if (await isAuditFirmIdentity(db, user, now))
        return none('skipped_audit_firm', `audit_firm: ${user}`);
    return {
        status: 'routed', approver_user_id: user, via: 'holder', delegate_user_id: null, delegation_id: null, reasons: [],
        trail: [{ user_id: user, responsibility: 'named_signer', via: 'holder', outcome: 'selected' }],
    };
}
function routingEvent(r, routing, actor, at) {
    return sodEventStatement({
        workspace_id: r.workspace_id, transition: 'T1', event: routing.status === 'routed' ? 'routed' : 'none_eligible', actor_user_id: actor,
        record_kind: 'signature_request', record_id: r.request_id, record_version: r.version, item_kind: r.artifact_kind, item_id: r.artifact_id,
        on_behalf_of_user_id: routing.delegate_user_id ? routing.approver_user_id : null, delegation_id: routing.delegation_id,
        detail: { approver_user_id: routing.approver_user_id, via: routing.via, delegate_user_id: routing.delegate_user_id, reasons: routing.reasons, trail: routing.trail },
        created_at: at,
    });
}
/**
 * R3-S2: the digest of what an artifact SAYS — canonical JSON with volatile
 * build stamps (`generated_at`) and the author's `change_note` left out, so a
 * rebuild of the same source (or a note added to the same content) digests
 * the same. Compared on resubmit: equal → 409 `unchanged`.
 */
export async function sourceSha256(content) {
    return sha256Hex(sourceCanonicalJson(content));
}
/** The exact bytes `sourceSha256` digests: canonical JSON without the volatile build stamp / change note. */
export function sourceCanonicalJson(content) {
    const strip = (v) => Array.isArray(v) ? v.map(strip)
        : v && typeof v === 'object'
            ? Object.fromEntries(Object.entries(v).filter(([k]) => k !== 'generated_at' && k !== 'change_note').map(([k, x]) => [k, strip(x)]))
            : v;
    return canonicalJson(strip(content));
}
/** The source digest of a frozen version (its stored content; else its content digest). */
export async function frozenSourceSha256(r) {
    return r.frozen_content_json === null ? r.content_sha256 : sourceSha256(JSON.parse(r.frozen_content_json));
}
function optDate(v, field) {
    if (v == null)
        return null;
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime()))
        throw new SignatureValidationError(`invalid_${field}`, `${field} is not a valid date`);
    return d.toISOString();
}
/**
 * Freeze a new version and request a signature. Any still-open request for
 * an earlier version of the same artifact becomes obsolete.
 */
export async function createSignatureRequest(db, input) {
    if (!isSignableArtifactKind(input.artifact_kind)) {
        throw new SignatureValidationError('invalid_artifact_kind', `Unknown artifact kind: ${String(input.artifact_kind)}`);
    }
    const artifactId = checkId(input.artifact_id, 'artifact_id');
    const title = (input.title ?? '').trim();
    if (!title || title.length > 300)
        throw new SignatureValidationError('invalid_title', 'title must be 1–300 characters');
    const factsVersion = input.facts_version == null ? null : checkId(input.facts_version, 'facts_version');
    const preparer = input.preparer_user_id ?? input.requested_by_user_id;
    for (const u of [preparer, input.requested_by_user_id]) {
        if (!(await isActiveMember(db, input.workspace_id, u))) {
            throw new SignatureValidationError('not_member', 'The preparer and requester must be active members of the workspace');
        }
    }
    if (!(await subjectExists(db, input.workspace_id, input.approver_subject))) {
        throw new SignatureValidationError('approver_subject_not_found', `Approver subject not found: ${input.approver_subject.kind}:${input.approver_subject.id}`);
    }
    const named = input.named_signer_user_id ?? null;
    if (NAMED_SIGNER_KINDS.has(input.artifact_kind)) {
        if (!named)
            throw new SignatureValidationError('named_signer_required', `A ${input.artifact_kind} request names the one person who signs it.`);
        if (input.store_content === false)
            throw new SignatureValidationError('content_required', 'A named-signer request keeps its frozen content.');
        if (input.content?.official_user_id !== named) {
            throw new SignatureValidationError('named_signer_mismatch', 'The frozen content must name the same signer (official_user_id).');
        }
        if (named === preparer || named === input.requested_by_user_id) {
            throw new SignatureForbiddenError('preparer', 'The preparer or requester of an item can never be its signer.');
        }
    }
    else if (named) {
        throw new SignatureValidationError('named_signer_not_allowed', `${input.artifact_kind} requests route to the approver hat, not a named signer.`);
    }
    const now = input.now ?? new Date();
    const nowIso = now.toISOString();
    const contentJson = canonicalJson(input.content);
    const contentSha = await sha256Hex(contentJson);
    const prev = await db.query(`SELECT MAX(version) AS v FROM signature_requests WHERE workspace_id = ? AND artifact_kind = ? AND artifact_id = ?`, [input.workspace_id, input.artifact_kind, artifactId]);
    const version = Number(prev.results[0]?.v ?? 0) + 1;
    await markObsoleteForArtifact(db, {
        workspace_id: input.workspace_id, artifact_kind: input.artifact_kind, artifact_id: artifactId,
        reason: `Superseded by version ${version}`, actor_user_id: input.requested_by_user_id, now,
    });
    const requestId = input.request_id ?? generateRandomId('sig');
    const base = {
        request_id: requestId, workspace_id: input.workspace_id, artifact_kind: input.artifact_kind, artifact_id: artifactId,
        version, content_sha256: contentSha, facts_version: factsVersion,
    };
    const frozen = await signWithWorkspaceKey(db, input.sign, input.workspace_id, freezePayload(base));
    const routing = await routeApprover(db, input.workspace_id, input.approver_subject, { preparer_user_id: preparer, requested_by_user_id: input.requested_by_user_id }, now, input.artifact_kind, named);
    const dueAt = optDate(input.due_at, 'due_at');
    const hardDate = optDate(input.hard_date, 'hard_date');
    try {
        await db.batch([
            {
                sql: `INSERT INTO signature_requests (
                request_id, workspace_id, artifact_kind, artifact_id, version, title, frozen_content_json, content_sha256,
                facts_version, frozen_key_id, frozen_hash, frozen_at, preparer_user_id, requested_by_user_id,
                approver_subject_kind, approver_subject_id, routing_status, approver_user_id, routing_json, state,
                created_at, updated_at, due_at, hard_date
              ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'requested', ?, ?, ?, ?)`,
                params: [
                    requestId, input.workspace_id, input.artifact_kind, artifactId, version, title,
                    input.store_content === false ? null : contentJson, contentSha, factsVersion, frozen.key_id, frozen.signature, nowIso,
                    preparer, input.requested_by_user_id, input.approver_subject.kind, input.approver_subject.id,
                    routing.status, routing.approver_user_id, JSON.stringify(routing.trail), nowIso, nowIso, dueAt, hardDate,
                ],
            },
            routingEvent({ workspace_id: input.workspace_id, request_id: requestId, version, artifact_kind: input.artifact_kind, artifact_id: artifactId }, routing, input.requested_by_user_id, nowIso),
            eventInsert({
                workspace_id: input.workspace_id, request_id: requestId, version, event: 'requested', actor_user_id: input.requested_by_user_id,
                on_behalf_of_user_id: null, delegation_id: null, reason: input.change_note ? String(input.change_note).trim().slice(0, REASON_MAX) || null : null, before_json: null,
                after_json: JSON.stringify({ routing_status: routing.status, approver_user_id: routing.approver_user_id, content_sha256: contentSha, facts_version: factsVersion }),
                at: nowIso,
            }),
        ]);
    }
    catch (err) {
        if (/unique|duplicate/i.test(String(err?.message ?? err))) {
            throw new SignatureStateError('concurrent_change', 'Another version of this artifact was frozen at the same time; retry.');
        }
        throw err;
    }
    await closeReviseItems(db, input.workspace_id, input.artifact_kind, artifactId, requestId, input.requested_by_user_id, now);
    return load(db, input.workspace_id, requestId);
}
/** S3: a new frozen version answers every open "revise" work item filed by a send-back of an earlier version. */
async function closeReviseItems(db, workspaceId, kind, artifactId, newRequestId, actor, now) {
    const open = await db.query(`SELECT w.work_item_id FROM work_items w
       JOIN signature_requests r ON r.request_id = w.source_id AND r.workspace_id = w.workspace_id
      WHERE w.workspace_id = ? AND w.status = 'open' AND w.source_kind = 'signature_request'
        AND r.artifact_kind = ? AND r.artifact_id = ? AND r.state = 'returned'`, [workspaceId, kind, artifactId]);
    for (const w of open.results) {
        try {
            await completeWorkItem(db, { workspace_id: workspaceId, work_item_id: w.work_item_id, actor_user_id: actor, completion_ref: `signature_request:${newRequestId}`, now });
        }
        catch { /* closed concurrently */ }
    }
}
/**
 * S3: the author revises a returned request and resubmits it — version v+1 of
 * the same artifact is frozen and re-routed (same approver subject, preparer,
 * requester, due and hard dates); the revise work item closes. Only the
 * preparer or the requester; only the latest version; only when returned.
 */
export async function resubmitSignatureRequest(db, input) {
    const r = await load(db, input.workspace_id, input.request_id);
    if (input.actor_user_id !== r.preparer_user_id && input.actor_user_id !== r.requested_by_user_id) {
        throw new SignatureForbiddenError('not_requester', 'Only the author (preparer) or the requester can resubmit a returned request.');
    }
    if (r.state !== 'returned')
        throw new SignatureStateError('not_returned', `Only a returned request can be resubmitted; this one is ${r.state}`);
    // A CMMC affirmation's statement is frozen on its own record (ADR-0058 §5): a
    // returned one is prepared again from the current score, never re-versioned,
    // so the official's signature always binds the statement she was shown.
    if (r.artifact_kind === 'cmmc_affirmation') {
        throw new SignatureStateError('not_resubmittable', 'A returned affirmation is prepared again from the current score: request a new one.');
    }
    const latest = await db.query(`SELECT MAX(version) AS v FROM signature_requests WHERE workspace_id = ? AND artifact_kind = ? AND artifact_id = ?`, [r.workspace_id, r.artifact_kind, r.artifact_id]);
    if (Number(latest.results[0]?.v ?? 0) !== r.version) {
        throw new SignatureStateError('stale_version', 'A newer version of this item already exists; open that one.');
    }
    const content = input.content;
    if (content === undefined) {
        throw new SignatureValidationError('content_required', 'Revise the item first: there is no revised content to freeze.');
    }
    // R3-S2: a resubmit must carry a real revision — never the returned version again (a note alone is not one).
    const [was, now] = await Promise.all([frozenSourceSha256(r), r.frozen_content_json === null ? sha256Hex(canonicalJson(content)) : sourceSha256(content)]);
    if (was === now) {
        throw new SignatureStateError('unchanged', `Nothing changed since v${r.version} was sent back. Revise the item, then resubmit.`);
    }
    return createSignatureRequest(db, {
        workspace_id: r.workspace_id, artifact_kind: r.artifact_kind, artifact_id: r.artifact_id, title: input.title ?? r.title, content,
        store_content: true, facts_version: input.facts_version === undefined ? r.facts_version : input.facts_version,
        preparer_user_id: r.preparer_user_id, requested_by_user_id: r.requested_by_user_id,
        approver_subject: { kind: r.approver_subject_kind, id: r.approver_subject_id },
        sign: input.sign, due_at: r.due_at, hard_date: r.hard_date, change_note: input.change_note ?? null,
        named_signer_user_id: namedSignerOf(r), now: input.now,
    });
}
/**
 * V98-3 review R3-S3: a lead extends an open request's SOFT due date. The hard
 * date never moves (the filing / external deadline): a request with no soft
 * date, or a new due date past the hard date, is refused (409 `hard_date`).
 * Authorization (owner/admin or the program lead) is the caller's; the change
 * is an append-only `rerouted` event carrying before / after and the reason.
 */
export async function extendSignatureDueDate(db, input) {
    const r = await load(db, input.workspace_id, input.request_id);
    if (r.state !== 'requested')
        throw new SignatureStateError(r.state === 'obsolete' ? 'obsolete' : 'not_requested', `Request is ${r.state}`);
    const reason = cleanReason(input.reason, true);
    const now = input.now ?? new Date();
    const due = optDate(input.due_at, 'due_at');
    if (!r.due_at) {
        throw new SignatureStateError('hard_date', 'Only a hard date is set, and a hard date can’t be extended. Reassign the signer or ask them to sign remotely.');
    }
    if (r.hard_date && due > r.hard_date) {
        throw new SignatureStateError('hard_date', `The hard date (${r.hard_date.slice(0, 10)}) can’t move; extend the due date up to it at most.`);
    }
    if (due <= r.due_at)
        throw new SignatureValidationError('not_later', 'The new due date must be later than the current one.');
    if (due <= now.toISOString())
        throw new SignatureValidationError('in_past', 'The new due date must be in the future.');
    const nowIso = now.toISOString();
    await transition(db, {
        sql: `UPDATE signature_requests SET due_at = ?, updated_at = ? WHERE request_id = ? AND workspace_id = ? AND state = 'requested' AND updated_at = ?`,
        params: [due, nowIso, r.request_id, r.workspace_id, r.updated_at],
    }, {
        workspace_id: r.workspace_id, request_id: r.request_id, version: r.version, event: 'rerouted', actor_user_id: input.actor_user_id,
        on_behalf_of_user_id: null, delegation_id: null, reason: `Due date extended: ${reason}`, before_json: JSON.stringify({ due_at: r.due_at }),
        after_json: JSON.stringify({ due_at: due, hard_date: r.hard_date }), at: nowIso,
    }, { state: 'requested', updated_at: nowIso });
    return load(db, r.workspace_id, r.request_id);
}
/** Re-run routing on an open request (e.g. after an approver is assigned to a none_eligible one). */
export async function rerouteSignatureRequest(db, input) {
    const r = await load(db, input.workspace_id, input.request_id);
    if (r.state !== 'requested')
        throw new SignatureStateError(r.state === 'obsolete' ? 'obsolete' : 'not_requested', `Request is ${r.state}`);
    const now = input.now ?? new Date();
    const nowIso = now.toISOString();
    const routing = await routeApprover(db, r.workspace_id, { kind: r.approver_subject_kind, id: r.approver_subject_id }, { preparer_user_id: r.preparer_user_id, requested_by_user_id: r.requested_by_user_id }, now, r.artifact_kind, namedSignerOf(r));
    await transition(db, {
        sql: `UPDATE signature_requests SET routing_status = ?, approver_user_id = ?, routing_json = ?, updated_at = ?
           WHERE request_id = ? AND workspace_id = ? AND state = 'requested' AND updated_at = ?`,
        params: [routing.status, routing.approver_user_id, JSON.stringify(routing.trail), nowIso, r.request_id, r.workspace_id, r.updated_at],
    }, {
        workspace_id: r.workspace_id, request_id: r.request_id, version: r.version, event: 'rerouted', actor_user_id: input.actor_user_id,
        on_behalf_of_user_id: null, delegation_id: null, reason: null, before_json: snap(r),
        after_json: JSON.stringify({ routing_status: routing.status, approver_user_id: routing.approver_user_id }), at: nowIso,
    }, { state: 'requested', updated_at: nowIso });
    await db.batch([routingEvent(r, routing, input.actor_user_id, nowIso)]);
    return load(db, r.workspace_id, r.request_id);
}
/**
 * S5: route an open request to the approver of record who agreed (a
 * `sign_remote` ask) to sign while away. The person must hold the approver
 * authority on the request's subject (hat or escalation chain — never the
 * owner/admin fallback for a non-delegable kind), be an eligible member, and
 * sit outside the item's pairs; the away-skip of T1 doesn't apply to the one
 * signing. Writes the reroute event and a T1 sod event (`via: remote_sign`).
 */
export async function routeToRemoteSigner(db, input) {
    const r = await load(db, input.workspace_id, input.request_id);
    if (r.state !== 'requested')
        throw new SignatureStateError(r.state === 'obsolete' ? 'obsolete' : 'not_requested', `Request is ${r.state}`);
    const now = input.now ?? new Date();
    const u = input.approver_user_id;
    if (u === r.preparer_user_id || u === r.requested_by_user_id) {
        throw new SignatureForbiddenError('preparer', 'The preparer or requester of an item can never approve it.');
    }
    const { step, parties } = signatureStep(r.artifact_kind, r.preparer_user_id);
    if (conflictsFor(step, parties).has(u))
        throw new SignatureForbiddenError('preparer', 'You are in a segregation-of-duties pair with this item.');
    const subject = { kind: r.approver_subject_kind, id: r.approver_subject_id };
    if (NAMED_SIGNER_KINDS.has(r.artifact_kind)) {
        if (u !== namedSignerOf(r))
            throw new SignatureForbiddenError('not_approver', 'Only the named signer can sign this item.');
    }
    else if (!(await isAuthorizedApprover(db, r.workspace_id, subject, u, now, { category: r.artifact_kind }))) {
        throw new SignatureForbiddenError('not_approver', 'You no longer hold the approver authority on this item.');
    }
    if (!(await isActiveMember(db, r.workspace_id, u)) || (await isAuditFirmIdentity(db, u, now))) {
        throw new SignatureForbiddenError('not_approver', 'The signer must be an eligible member of the workspace.');
    }
    if (r.routing_status === 'routed' && r.approver_user_id === u)
        return r;
    const nowIso = now.toISOString();
    const trail = [{ user_id: u, responsibility: 'approver', via: 'holder', outcome: 'selected' }];
    await transition(db, {
        sql: `UPDATE signature_requests SET routing_status = 'routed', approver_user_id = ?, routing_json = ?, updated_at = ?
           WHERE request_id = ? AND workspace_id = ? AND state = 'requested' AND updated_at = ?`,
        params: [u, JSON.stringify(trail), nowIso, r.request_id, r.workspace_id, r.updated_at],
    }, {
        workspace_id: r.workspace_id, request_id: r.request_id, version: r.version, event: 'rerouted', actor_user_id: u,
        on_behalf_of_user_id: null, delegation_id: null, reason: `Signing remotely (ask ${input.ask_id})`, before_json: snap(r),
        after_json: JSON.stringify({ routing_status: 'routed', approver_user_id: u, via: 'remote_sign' }), at: nowIso,
    }, { state: 'requested', updated_at: nowIso });
    await db.batch([sodEventStatement({
            workspace_id: r.workspace_id, transition: 'T1', event: 'routed', actor_user_id: u, record_kind: 'signature_request', record_id: r.request_id,
            record_version: r.version, item_kind: r.artifact_kind, item_id: r.artifact_id,
            detail: { approver_user_id: u, via: 'remote_sign', ask_id: input.ask_id }, created_at: nowIso,
        })]);
    return load(db, r.workspace_id, r.request_id);
}
/**
 * The acting approver must be the routed approver of record and still hold
 * the authority (the hat, or a place in a holder's escalation chain), or act
 * for them under a verified delegation. While a live delegation covers the
 * item, the approver of record is locked out until they take it back.
 */
async function assertApprover(db, r, actor, now, delegation) {
    if (actor === r.preparer_user_id || actor === r.requested_by_user_id) {
        throw new SignatureForbiddenError('preparer', 'The preparer or requester of an item can never approve it.');
    }
    if (r.routing_status !== 'routed' || !r.approver_user_id) {
        throw new SignatureStateError('none_eligible', 'No eligible approver is routed for this request.');
    }
    const named = NAMED_SIGNER_KINDS.has(r.artifact_kind);
    if (named && delegation) {
        throw new SignatureForbiddenError('delegated', 'Only the named signer can sign this item; it cannot be signed under a delegation.');
    }
    const approver = delegation ? delegation.on_behalf_of_user_id : actor;
    if (approver !== r.approver_user_id) {
        throw new SignatureForbiddenError('not_approver', 'Only the routed approver can act on this request.');
    }
    const subject = { kind: r.approver_subject_kind, id: r.approver_subject_id };
    if (named) {
        // ADR-0058 §5: the person the frozen content names, and nobody else.
        if (actor !== namedSignerOf(r))
            throw new SignatureForbiddenError('not_approver', 'Only the named signer can sign this item.');
    }
    else if (!(await isAuthorizedApprover(db, r.workspace_id, subject, approver, now, { category: r.artifact_kind }))) {
        throw new SignatureForbiddenError('not_approver', 'The routed approver no longer holds the approver hat; re-route the request.');
    }
    if (!(await isActiveMember(db, r.workspace_id, actor)) || (await isAuditFirmIdentity(db, actor, now))) {
        throw new SignatureForbiddenError('not_approver', 'The signer must be an eligible member of the workspace.');
    }
    if (delegation) {
        if (delegation.on_behalf_of_user_id === r.preparer_user_id) {
            throw new SignatureForbiddenError('preparer', 'A delegation can never satisfy the preparer ↔ approver pair.');
        }
        const verify = delegation.verifier ?? ((d, v) => verifyDelegationForRequest(d, v, now));
        const ok = await verify(db, {
            workspace_id: r.workspace_id, delegation_id: delegation.delegation_id, delegator_user_id: delegation.on_behalf_of_user_id,
            delegate_user_id: actor, request: r,
        });
        if (!ok)
            throw new SignatureForbiddenError('not_approver', 'The delegation does not cover this request.');
    }
    else if (await effectiveDelegationFor(db, r.workspace_id, actor, r.artifact_kind, now)) {
        throw new SignatureForbiddenError('delegated', 'You delegated this item; take it back (end the delegation) before acting on it.');
    }
}
export async function signSignatureRequest(db, input) {
    if (!SIGNATURE_METHODS.includes(input.method)) {
        throw new SignatureValidationError('invalid_method', 'method must be passkey or code');
    }
    if ((input.on_behalf_of_user_id == null) !== (input.delegation_id == null)) {
        throw new SignatureValidationError('invalid_delegation', 'on_behalf_of_user_id and delegation_id go together');
    }
    const r = await load(db, input.workspace_id, input.request_id);
    if (r.state === 'obsolete')
        throw new SignatureStateError('obsolete', `Version ${r.version} is obsolete: ${r.reason ?? ''}`.trim());
    if (r.state !== 'requested')
        throw new SignatureStateError('not_requested', `Request is ${r.state}`);
    if (input.version !== r.version || input.content_sha256 !== r.content_sha256) {
        throw new SignatureStateError('stale_version', 'The version you reviewed is not the frozen version.');
    }
    const now = input.now ?? new Date();
    const nowIso = now.toISOString();
    await assertPackagePrerequisitesClear(db, r.workspace_id, r, now);
    const delegation = input.on_behalf_of_user_id && input.delegation_id
        ? { on_behalf_of_user_id: input.on_behalf_of_user_id, delegation_id: input.delegation_id, verifier: input.delegationVerifier }
        : undefined;
    await assertApprover(db, r, input.actor_user_id, now, delegation);
    if (input.current_facts_version !== undefined && (input.current_facts_version ?? null) !== r.facts_version) {
        await markObsoleteForArtifact(db, {
            workspace_id: r.workspace_id, artifact_kind: r.artifact_kind, artifact_id: r.artifact_id,
            reason: 'Facts changed since freeze', actor_user_id: input.actor_user_id, now,
        });
        throw new SignatureStateError('obsolete', 'The facts this version depends on changed; re-freeze it.');
    }
    let undoUntil = null;
    if (input.undo_until != null) {
        const d = input.undo_until instanceof Date ? input.undo_until : new Date(input.undo_until);
        if (Number.isNaN(d.getTime()) || d.getTime() <= now.getTime()) {
            throw new SignatureValidationError('invalid_undo_until', 'undo_until must be a future date');
        }
        undoUntil = d.toISOString();
    }
    // V98-7-3 M9: the assertion isn't verified, so the record (and the seal) says `session`.
    const signedFields = {
        signed_by_user_id: input.actor_user_id, signed_at: nowIso, method: 'session',
        on_behalf_of_user_id: delegation?.on_behalf_of_user_id ?? null, delegation_id: delegation?.delegation_id ?? null,
    };
    const sig = await signWithWorkspaceKey(db, input.sign, r.workspace_id, signaturePayload({ ...r, ...signedFields }));
    await transition(db, {
        sql: `UPDATE signature_requests
             SET state = 'signed', signed_by_user_id = ?, signed_at = ?, signature_key_id = ?, signature_hmac = ?, method = ?, method_claimed = ?,
                 on_behalf_of_user_id = ?, delegation_id = ?, undo_until = ?, decided_at = ?, reason = NULL, updated_at = ?
           WHERE request_id = ? AND workspace_id = ? AND state = 'requested' AND version = ? AND updated_at = ?`,
        params: [
            signedFields.signed_by_user_id, nowIso, sig.key_id, sig.signature, signedFields.method, input.method, signedFields.on_behalf_of_user_id,
            signedFields.delegation_id, undoUntil, nowIso, nowIso, r.request_id, r.workspace_id, r.version, r.updated_at,
        ],
    }, {
        workspace_id: r.workspace_id, request_id: r.request_id, version: r.version, event: 'signed', actor_user_id: input.actor_user_id,
        on_behalf_of_user_id: signedFields.on_behalf_of_user_id, delegation_id: signedFields.delegation_id, reason: null,
        before_json: snap(r),
        after_json: JSON.stringify({ signed_by_user_id: input.actor_user_id, method: signedFields.method, method_claimed: input.method, signature_key_id: sig.key_id, undo_until: undoUntil, facts_version: r.facts_version, content_sha256: r.content_sha256 }),
        at: nowIso,
    }, { state: 'signed', updated_at: nowIso });
    return load(db, r.workspace_id, r.request_id);
}
/** The routed approver returns the item to its author with a reason (state `returned`). */
export async function sendBackSignatureRequest(db, input) {
    const reason = cleanReason(input.reason, true);
    const r = await load(db, input.workspace_id, input.request_id);
    if (r.state === 'obsolete')
        throw new SignatureStateError('obsolete', `Version ${r.version} is obsolete`);
    if (r.state !== 'requested')
        throw new SignatureStateError('not_requested', `Request is ${r.state}`);
    const now = input.now ?? new Date();
    await assertApprover(db, r, input.actor_user_id, now);
    const nowIso = now.toISOString();
    await transition(db, {
        sql: `UPDATE signature_requests SET state = 'returned', reason = ?, decided_at = ?, updated_at = ?
           WHERE request_id = ? AND workspace_id = ? AND state = 'requested' AND updated_at = ?`,
        params: [reason, nowIso, nowIso, r.request_id, r.workspace_id, r.updated_at],
    }, {
        workspace_id: r.workspace_id, request_id: r.request_id, version: r.version, event: 'sent_back', actor_user_id: input.actor_user_id,
        on_behalf_of_user_id: null, delegation_id: null, reason, before_json: snap(r), after_json: JSON.stringify({ state: 'returned' }), at: nowIso,
    }, { state: 'returned', updated_at: nowIso });
    // The author owes the next action: revise and resubmit (filed on their owed list).
    try {
        await createWorkItem(db, {
            workspace_id: r.workspace_id, kind: 'task', title: `Revise and resubmit · ${r.title}`.slice(0, 300),
            detail: `Returned by the approver: ${reason}`.slice(0, 4000), source_kind: 'signature_request', source_id: r.request_id,
            due_at: r.due_at, hard_date: r.hard_date, owner_user_id: r.preparer_user_id, actor_user_id: input.actor_user_id, now,
        });
    }
    catch (err) {
        // A preparer who can no longer hold work (left the workspace; T17) still
        // sees the request itself; the send-back stands.
        const code = err?.code;
        if (code !== 'not_member' && code !== 'audit_firm_member')
            throw err;
    }
    return load(db, r.workspace_id, r.request_id);
}
/** The requester (or an admin, as decided by the route) withdraws an open request. */
export async function withdrawSignatureRequest(db, input) {
    const reason = cleanReason(input.reason, true);
    const r = await load(db, input.workspace_id, input.request_id);
    if (!input.actor_is_admin && input.actor_user_id !== r.requested_by_user_id) {
        throw new SignatureForbiddenError('not_requester', 'Only the requester (or an admin) can withdraw this request.');
    }
    if (r.state !== 'requested')
        throw new SignatureStateError(r.state === 'obsolete' ? 'obsolete' : 'not_requested', `Request is ${r.state}`);
    const nowIso = (input.now ?? new Date()).toISOString();
    await transition(db, {
        sql: `UPDATE signature_requests SET state = 'withdrawn', reason = ?, decided_at = ?, updated_at = ?
           WHERE request_id = ? AND workspace_id = ? AND state = 'requested' AND updated_at = ?`,
        params: [reason, nowIso, nowIso, r.request_id, r.workspace_id, r.updated_at],
    }, {
        workspace_id: r.workspace_id, request_id: r.request_id, version: r.version, event: 'withdrawn', actor_user_id: input.actor_user_id,
        on_behalf_of_user_id: null, delegation_id: null, reason, before_json: snap(r), after_json: JSON.stringify({ state: 'withdrawn' }), at: nowIso,
    }, { state: 'withdrawn', updated_at: nowIso });
    return load(db, r.workspace_id, r.request_id);
}
/** Can this signed request still be undone? (No send marker yet, and before undo_until.) */
export function undoAllowed(r, now = new Date()) {
    if (r.state !== 'signed' || r.downstream_sent_at)
        return false;
    return !r.undo_until || now.getTime() < Date.parse(r.undo_until);
}
/** The signer takes the signature back: signed → requested (same frozen version), logged. */
export async function undoSignature(db, input) {
    const reason = cleanReason(input.reason, false);
    const r = await load(db, input.workspace_id, input.request_id);
    if (r.state !== 'signed')
        throw new SignatureStateError('not_signed', `Request is ${r.state}`);
    if (input.actor_user_id !== r.signed_by_user_id) {
        throw new SignatureForbiddenError('not_signer', 'Only the signer can undo this signature.');
    }
    const now = input.now ?? new Date();
    if (!undoAllowed(r, now)) {
        throw new SignatureStateError('undo_locked', r.downstream_sent_at ? 'The signed item has already been sent.' : 'The undo window has closed.');
    }
    const nowIso = now.toISOString();
    await transition(db, {
        sql: `UPDATE signature_requests
             SET state = 'requested', signed_by_user_id = NULL, signed_at = NULL, signature_key_id = NULL, signature_hmac = NULL,
                 method = NULL, on_behalf_of_user_id = NULL, delegation_id = NULL, undo_until = NULL, decided_at = NULL, updated_at = ?
           WHERE request_id = ? AND workspace_id = ? AND state = 'signed' AND downstream_sent_at IS NULL AND updated_at = ?`,
        params: [nowIso, r.request_id, r.workspace_id, r.updated_at],
    }, {
        workspace_id: r.workspace_id, request_id: r.request_id, version: r.version, event: 'undone', actor_user_id: input.actor_user_id,
        on_behalf_of_user_id: null, delegation_id: null, reason, before_json: snap(r), after_json: JSON.stringify({ state: 'requested' }), at: nowIso,
    }, { state: 'requested', updated_at: nowIso });
    return load(db, r.workspace_id, r.request_id);
}
/**
 * The downstream send / filing marker: the signed item left the building.
 * After this the row is final (undo locked; a DB trigger blocks any update).
 */
export async function markDownstreamSent(db, input) {
    const r = await load(db, input.workspace_id, input.request_id);
    if (!input.actor_is_admin && input.actor_user_id !== r.requested_by_user_id && input.actor_user_id !== r.signed_by_user_id) {
        throw new SignatureForbiddenError('not_requester', 'Only the requester, the signer or an admin can record the send.');
    }
    if (r.state !== 'signed')
        throw new SignatureStateError('not_signed', `Request is ${r.state}`);
    if (r.downstream_sent_at)
        throw new SignatureStateError('already_sent', 'Already sent.');
    const nowIso = (input.now ?? new Date()).toISOString();
    const ref = input.downstream_ref == null ? null : checkId(input.downstream_ref, 'downstream_ref');
    const rec = input.signed_record_ref == null ? null : checkId(input.signed_record_ref, 'signed_record_ref');
    await transition(db, {
        sql: `UPDATE signature_requests SET downstream_sent_at = ?, downstream_ref = ?, signed_record_ref = ?, updated_at = ?
           WHERE request_id = ? AND workspace_id = ? AND state = 'signed' AND downstream_sent_at IS NULL AND updated_at = ?`,
        params: [nowIso, ref, rec, nowIso, r.request_id, r.workspace_id, r.updated_at],
    }, {
        workspace_id: r.workspace_id, request_id: r.request_id, version: r.version, event: 'downstream_sent', actor_user_id: input.actor_user_id,
        on_behalf_of_user_id: null, delegation_id: null, reason: null, before_json: snap(r),
        after_json: JSON.stringify({ downstream_ref: ref, signed_record_ref: rec }), at: nowIso,
    }, { state: 'signed', updated_at: nowIso });
    return load(db, r.workspace_id, r.request_id);
}
/**
 * A prerequisite (or the facts) changed: every OPEN (`requested`) request on
 * the artifact becomes obsolete and can no longer be signed. Signed versions
 * stay signed (they are historical records); re-freeze to get a new version.
 */
export async function markObsoleteForArtifact(db, input) {
    const reason = cleanReason(input.reason, true);
    const open = await db.query(`SELECT * FROM signature_requests WHERE workspace_id = ? AND artifact_kind = ? AND artifact_id = ? AND state = 'requested'
      ORDER BY version ASC`, [input.workspace_id, input.artifact_kind, input.artifact_id]);
    const nowIso = (input.now ?? new Date()).toISOString();
    const out = [];
    for (const r of open.results) {
        const res = await db.batch([
            {
                sql: `UPDATE signature_requests SET state = 'obsolete', reason = ?, decided_at = ?, updated_at = ?
               WHERE request_id = ? AND workspace_id = ? AND state = 'requested'`,
                params: [reason, nowIso, nowIso, r.request_id, r.workspace_id],
            },
            eventInsert({
                workspace_id: r.workspace_id, request_id: r.request_id, version: r.version, event: 'obsolete', actor_user_id: input.actor_user_id,
                on_behalf_of_user_id: null, delegation_id: null, reason, before_json: snap(r), after_json: JSON.stringify({ state: 'obsolete' }), at: nowIso,
            }, { state: 'obsolete', updated_at: nowIso }),
        ]);
        if ((res?.[0]?.meta?.changes ?? 1) > 0)
            out.push(await load(db, r.workspace_id, r.request_id));
    }
    return out;
}
// ---- reads ---------------------------------------------------------------------
export async function getSignatureRequest(db, workspaceId, requestId) {
    const r = await db.query(`SELECT * FROM signature_requests WHERE request_id = ? AND workspace_id = ? LIMIT 1`, [requestId, workspaceId]);
    const row = r.results[0];
    if (!row)
        return null;
    // V98-7-3 M9: the public read presents the method (`session`) and keeps the claimed one server-side; integrity
    // checks (verifySignatureRequest) read the sealed row through `load`.
    const { method_claimed: _claimed, ...rest } = row;
    void _claimed;
    return { ...rest, method: presentedSignatureMethod(row.method) };
}
/** Metadata only (no frozen content), newest first. */
export async function listSignatureRequests(db, workspaceId, f = {}) {
    const clauses = [];
    const params = [workspaceId];
    if (f.artifact_kind) {
        clauses.push('artifact_kind = ?');
        params.push(f.artifact_kind);
    }
    if (f.artifact_id) {
        clauses.push('artifact_id = ?');
        params.push(f.artifact_id);
    }
    // d1-in-list: bounded — states is a subset of the SIGNATURE_STATES enum (route validates it)
    const states = f.state === undefined ? [] : Array.isArray(f.state) ? f.state : [f.state];
    if (states.length) {
        clauses.push(`state IN (${states.map(() => '?').join(', ')})`);
        params.push(...states);
    }
    if (f.approver_user_id) {
        clauses.push('approver_user_id = ?');
        params.push(f.approver_user_id);
    }
    if (f.requested_by_user_id) {
        clauses.push('requested_by_user_id = ?');
        params.push(f.requested_by_user_id);
    }
    const limit = Math.min(Math.max(f.limit ?? 100, 1), 500);
    const r = await db.query(`SELECT request_id, workspace_id, artifact_kind, artifact_id, version, title, content_sha256, facts_version, frozen_key_id,
            frozen_hash, frozen_at, preparer_user_id, requested_by_user_id, approver_subject_kind, approver_subject_id,
            routing_status, approver_user_id, routing_json, state, reason, signed_by_user_id, signed_at, signature_key_id,
            signature_hmac, method, on_behalf_of_user_id, delegation_id, undo_until, downstream_sent_at, downstream_ref,
            signed_record_ref, decided_at, created_at, updated_at
       FROM signature_requests WHERE workspace_id = ?${clauses.map((cl) => ' AND ' + cl).join('')}
      ORDER BY created_at DESC, version DESC, request_id ASC LIMIT ${limit}`, params);
    // V98-7-3 M9: reads for display never carry an unverified "passkey".
    return r.results.map((x) => ({ ...x, method: presentedSignatureMethod(x.method) }));
}
export async function listSignatureEvents(db, workspaceId, requestId) {
    const r = await db.query(`SELECT * FROM signature_request_events WHERE workspace_id = ? AND request_id = ? ORDER BY at ASC, event_id ASC`, [workspaceId, requestId]);
    return r.results;
}
/** The lead's view of an artifact: its latest version's state (send-backs and undos show here). */
export async function getArtifactSignatureStatus(db, workspaceId, artifact) {
    const all = await listSignatureRequests(db, workspaceId, { artifact_kind: artifact.kind, artifact_id: artifact.id, limit: 500 });
    const latest = all.reduce((a, b) => (!a || b.version > a.version ? b : a), null);
    return { latest, versions: all.length, undo_allowed: latest ? undoAllowed(latest) : false };
}
/**
 * Re-derive both HMACs with the stored keys (inside the signer) and compare in
 * constant time; also re-hash the stored content when it was kept.
 */
export async function verifySignatureRequest(db, workspaceId, requestId, sign) {
    const r = await load(db, workspaceId, requestId);
    const content_matches = r.frozen_content_json === null ? null : (await sha256Hex(r.frozen_content_json)) === r.content_sha256;
    const resign = async (data, keyId, expected) => {
        try {
            const again = await sign(r.workspace_id, data, keyId);
            return again.key_id === keyId && timingSafeEqualHex(again.signature, expected);
        }
        catch {
            return false;
        }
    };
    const frozen_valid = await resign(freezePayload(r), r.frozen_key_id, r.frozen_hash);
    const signature_valid = r.state === 'signed' && r.signature_hmac && r.signature_key_id && r.signed_by_user_id && r.signed_at && r.method
        ? await resign(signaturePayload({ ...r, signed_by_user_id: r.signed_by_user_id, signed_at: r.signed_at, method: r.method }), r.signature_key_id, r.signature_hmac)
        : null;
    return { content_matches, frozen_valid, signature_valid };
}
//# sourceMappingURL=signature-request-service.js.map